import {Link, RouteComponentProps} from '@reach/router';
import {Column} from 'material-table';
import React from 'react';
import {Chip} from '@material-ui/core';
import {ExportButton} from '../../Components/Buttons/ExportButton';
import Row from '../../Components/Layout/Row';
import {RowOfButtons} from '../../Components/Layout/RowOfButtons';
import PageTitle from '../../Components/PageTitle/PageTitle';
import {buildUniversalEntityFilter} from '../../Components/UniversalRListTable/Filters/buildUniversalEntityFilter';
import {buildUniversalTagsFilter} from '../../Components/UniversalRListTable/Filters/buildUniversalTagsFilter';
import {DistrictContext} from '../../Components/UniversalRListTable/Helpers/DistrictContext';
import {useDistrictState} from '../../Components/UniversalRListTable/Helpers/useDistrictState';
import {UniversalRListTable} from '../../Components/UniversalRListTable/UniversalRListTable';
import {timestampToDateTime} from '../../Localization/DateFormatter';

interface EventSubjectsCellProps {
  eventSubjects: any[];
}

/**
 * Renders event subjects inside the table cell as chips,
 * initially showing only first two and expanding inside the cell on demand.
 * @param {EventSubjectsCellProps} props
 * @return {JSX.Element | null}
 */
function EventSubjectsCell(props: EventSubjectsCellProps): JSX.Element | null {
  const {eventSubjects} = props;

  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!eventSubjects || !Array.isArray(eventSubjects) || !eventSubjects.length) {
    return null;
  }

  const titles = eventSubjects.map((el: any) => {
    if (typeof el === 'string' || typeof el === 'number') {
      return el;
    }
    return el?.name;
  }).filter((title: any) => Boolean(title));

  if (!titles.length) {
    return null;
  }

  const maxVisible = 2;
  const visibleTitles = isExpanded ? titles : titles.slice(0, maxVisible);
  const hiddenCount = isExpanded ? 0 : Math.max(titles.length - maxVisible, 0);

  const handleShowMore = (): void => {
    setIsExpanded(true);
  };

  return (
    <div style={{display: 'flex', flexWrap: 'wrap'}}>
      {visibleTitles.map((title: string) => (
        <Chip
          key={title}
          label={title}
          size="small"
          style={{marginRight: 4, marginBottom: 4}}
        />
      ))}
      {!isExpanded && hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount}`}
          size="small"
          color="primary"
          onClick={handleShowMore}
          clickable
          style={{marginRight: 4, marginBottom: 4}}
        />
      )}
    </div>
  );
}

/**
 * Result Upload's read list.
 * @param {RouteComponentProps} props
 * @returns {JSX.Element}
 * @constructor
 */
export default function ResultUploadsReadList(props: RouteComponentProps) {
  const DC = useDistrictState();

  const uploadPrefix = '/result-upload/';
  const userPrefix = '/user/';
  const eventPrefix = '/event/';

  const statusLookup = {
    1: 'Подтвержден',
    2: 'Отменена',
    3: 'Ошибка прав доступа',
    4: 'Ошибка формата загрузки',
    5: 'Ожидание разрешения конфликта',
  };
  const eventFilter = buildUniversalEntityFilter('EVENT_LIST', 3);
  const levelFilter = buildUniversalEntityFilter('LEVEL_LIST');
  const academicYearFilter = buildUniversalEntityFilter('ACADEMIC_YEAR_LIST');
  const eventSubjectsFilter = buildUniversalTagsFilter('EVENT_SUBJECTS_LIST');
  const districtsFilter = buildUniversalTagsFilter('DISTRICT_LIST');

  const Columns: Column<any>[] =
    [
      {
        title: '#',
        field: 'id',
        render: (rowData): React.ReactNode =>
          <Link to={`${uploadPrefix}${rowData.id}`}>{rowData.id}</Link>,
      },
      {
        title: 'Статус',
        field: 'status',
        lookup: statusLookup,
        render: (rowData): React.ReactNode =>
          rowData.status.name,
      },
      {
        title: 'Результаты',
        field: 'result_count',
        filtering: false,
        render: (rowData): React.ReactNode =>
          <Link
            to={`${uploadPrefix}${rowData.id}`}>{rowData.result_count}</Link>,
      },
      {
        title: 'Район',
        field: 'districts',
        sorting: false,
        filterComponent: districtsFilter,
        render: (rowData): React.ReactNode =>
          <EventSubjectsCell eventSubjects={rowData.districts}/>,
      },
      {
        title: 'Фамилия пользователя',
        field: 'user.last_name',
        render: (rowData): React.ReactNode =>
          <Link
            to={`${userPrefix}${rowData.user.id}`}>{rowData.user.last_name}</Link>,
      },
      {
        title: 'Имя пользователя',
        field: 'user.first_name',
        render: (rowData): React.ReactNode =>
          <Link
            to={`${userPrefix}${rowData.user.id}`}>{rowData.user.first_name}</Link>,
      },
      {
        title: 'Мероприятие',
        field: 'event.id',
        filterComponent: eventFilter,

        render: (rowData): React.ReactNode =>
          <Link
            to={`${eventPrefix}${rowData.event.id}`}>{rowData.event.name}</Link>,
      },
      {
        title: 'Предметы',
        field: 'event_subjects',
        sorting: false,
        filterComponent: eventSubjectsFilter,
        render: (rowData): React.ReactNode =>
          <EventSubjectsCell eventSubjects={rowData.event_subjects}/>,
      },
      {
        title: 'Уровень',
        field: 'event.level.name',
        filterComponent: levelFilter,
      },
      {
        title: 'Год',
        field: 'event.academic_year.name',
        filterComponent: academicYearFilter,
      },
      {
        title: 'Создана',
        field: 'created_at',
        render: (rowData): React.ReactNode =>
          timestampToDateTime(rowData.created_at),
        filtering: false,
      },
      {
        title: 'Обновлена',
        field: 'updated_at',
        render: (rowData): React.ReactNode =>
          timestampToDateTime(rowData.updated_at),
        filtering: false,
      },
    ];
  const exceptions = [
    'user.first_name',
    'user.last_name',
    'event.level.name',
    'event.academic_year.name',
  ];
  const lastQueryKeyName = 'resultUploadsReadList';

  return (
    <React.Fragment>
      <PageTitle title={'Загрузки результатов'}/>
      <RowOfButtons>
        <ExportButton name={lastQueryKeyName}/>
      </RowOfButtons>

      <Row>
        <DistrictContext.Provider value={DC}>
          <UniversalRListTable
            searchExceptions={exceptions}
            columns={Columns}
            action={'RESULT_UPLOADS_INDEX'}
            tableName={lastQueryKeyName}
          />
        </DistrictContext.Provider>
      </Row>

    </React.Fragment>
  );
}
